import { X, Edit3, Package, Calendar, Truck, ClipboardList, Hash, Building2, DollarSign, Store, Mail, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { TID } from "@/lib/testIds";

const STATUS_META = {
    pending: { bg: "#333333", fg: "#A1A1AA", label: "PENDING" },
    info_received: { bg: "#2979FF", fg: "#050505", label: "INFO RECEIVED" },
    in_transit: { bg: "#FFEA00", fg: "#050505", label: "IN TRANSIT" },
    out_for_delivery: { bg: "#FF5500", fg: "#050505", label: "OUT FOR DELIVERY" },
    delivered: { bg: "#00E676", fg: "#050505", label: "DELIVERED" },
    attempt_fail: { bg: "#FF1744", fg: "#FFFFFF", label: "ATTEMPT FAILED" },
    available_for_pickup: { bg: "#A855F7", fg: "#FFFFFF", label: "AT PICKUP" },
    exception: { bg: "#FF1744", fg: "#FFFFFF", label: "EXCEPTION" },
    expired: { bg: "#52525B", fg: "#FFFFFF", label: "EXPIRED" },
};

export function OrderStatusPill({ value, onClick }) {
    const s = STATUS_META[value] || STATUS_META.pending;
    return (
        <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center justify-center px-2 py-1 min-w-[110px] font-mono text-[10px] tracking-[0.16em] font-bold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: s.bg, color: s.fg }}
        >
            {s.label}
        </button>
    );
}

export default function OrderExpandOverlay({ open, order, onClose, onEdit }) {
    return (
        <AnimatePresence>
            {open && order && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-0 z-[85] flex items-center justify-center bg-black/85 backdrop-blur-md"
                    onClick={onClose}
                    data-testid={TID.orderExpandOverlay}
                >
                    <motion.div
                        initial={{ y: 16, scale: 0.98 }}
                        animate={{ y: 0, scale: 1 }}
                        exit={{ y: 16, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-[1200px] max-w-[92vw] max-h-[86vh] overflow-hidden bg-[#0A0A0A] border-2 border-virtus-orange"
                    >
                        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange" />
                        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange" />
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange" />
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange" />

                        {/* Header */}
                        <div className="flex items-center justify-between px-8 py-4 border-b border-virtus-line">
                            <div className="flex items-center gap-4">
                                <div className="font-mono text-xs tracking-[0.28em] uppercase text-virtus-orange">
                                    ORDER #{String(order.index || 0).padStart(2, "0")}
                                </div>
                                <span className="text-virtus-line">//</span>
                                <div className="font-mono text-xs tracking-[0.24em] uppercase text-virtus-dim">
                                    SOURCE: {(order.source || "manual").toUpperCase()}
                                </div>
                                {order.carrier && (
                                    <>
                                        <span className="text-virtus-line">//</span>
                                        <div className="font-mono text-xs tracking-[0.24em] uppercase text-virtus-dim">
                                            {order.carrier.toUpperCase()}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button
                                onClick={onClose}
                                className="flex items-center gap-2 px-3 py-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange border border-virtus-line hover:border-virtus-orange"
                                data-testid={TID.orderExpandCloseBtn}
                            >
                                <X size={12} /> CLOSE
                            </button>
                        </div>

                        {/* Big title */}
                        <div className="px-8 pt-8 pb-4">
                            <h1
                                className="font-heading font-black uppercase tracking-tighter leading-none text-white break-words"
                                style={{ fontSize: "clamp(40px, 5vw, 76px)" }}
                            >
                                {order.item}
                            </h1>
                        </div>

                        <div className="px-8 pb-6 flex items-center gap-6">
                            <div className="scale-[1.6] origin-left">
                                <OrderStatusPill value={order.status} />
                            </div>
                            <div className="ml-8 font-mono text-2xl font-bold text-virtus-orange">
                                × {order.quantity || 1}
                            </div>
                        </div>

                        <div className="grid grid-cols-5 gap-px bg-virtus-line border-t border-b border-virtus-line">
                            <DetailBlock icon={Calendar} label="Order date" value={order.order_date || "—"} />
                            <DetailBlock
                                icon={Truck}
                                label="Expected"
                                value={order.expected_delivery || (order.tracking_number ? "—" : "TBD after ship")}
                                accent={dueAccent(order.expected_delivery, order.status)}
                                muted={!order.expected_delivery && !order.tracking_number}
                            />
                            <DetailBlock
                                icon={Package}
                                label="Delivered"
                                value={order.delivered_at ? order.delivered_at.slice(0, 10) : "—"}
                                accent={order.delivered_at ? "#00E676" : null}
                            />
                            <DetailBlock
                                icon={Store}
                                label="Vendor"
                                value={order.vendor || (order.source || "manual").toUpperCase()}
                            />
                            <DetailBlock
                                icon={DollarSign}
                                label="Price"
                                value={
                                    typeof order.price === "number"
                                        ? `$${order.price.toFixed(2)}`
                                        : "—"
                                }
                                accent={typeof order.price === "number" ? "#FF8A3D" : null}
                            />
                        </div>

                        <div className="px-8 py-6 grid grid-cols-2 gap-8">
                            <div>
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                                    <Hash size={12} /> TRACKING #
                                </div>
                                {order.tracking_number ? (
                                    <div className="font-mono text-xl text-virtus-orange break-all">
                                        {order.tracking_number}
                                    </div>
                                ) : (
                                    <div className="italic text-virtus-faint text-base">
                                        Awaiting shipping email
                                    </div>
                                )}
                                {order.last_synced_at && (
                                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint mt-1">
                                        Last synced: {order.last_synced_at.replace("T", " ").slice(0, 19)}Z
                                    </div>
                                )}

                                {(order.vendor_contact_email || order.vendor_contact_phone) && (
                                    <div className="mt-6">
                                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                                            <Building2 size={12} /> VENDOR CONTACT
                                        </div>
                                        {order.vendor_contact_email && (
                                            <a
                                                href={`mailto:${order.vendor_contact_email}`}
                                                className="flex items-center gap-2 text-base text-white hover:text-virtus-orange break-all"
                                            >
                                                <Mail size={14} className="flex-shrink-0 text-virtus-faint" />
                                                {order.vendor_contact_email}
                                            </a>
                                        )}
                                        {order.vendor_contact_phone && (
                                            <a
                                                href={`tel:${order.vendor_contact_phone.replace(/[^0-9+]/g, "")}`}
                                                className="mt-1.5 flex items-center gap-2 text-base text-white hover:text-virtus-orange"
                                            >
                                                <Phone size={14} className="flex-shrink-0 text-virtus-faint" />
                                                {order.vendor_contact_phone}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                                    <ClipboardList size={12} /> NOTES
                                </div>
                                <div className="text-lg leading-relaxed text-white whitespace-pre-wrap min-h-[48px]">
                                    {order.notes && order.notes.trim()
                                        ? order.notes
                                        : "No notes on record."}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between px-8 py-4 border-t border-virtus-line">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                                UPDATED:{" "}
                                <span className="text-virtus-dim">
                                    {order.updated_at
                                        ? order.updated_at.replace("T", " ").slice(0, 19) + "Z"
                                        : "—"}
                                </span>
                            </div>
                            <button
                                onClick={onEdit}
                                className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase bg-virtus-orange text-black hover:bg-white glow-orange"
                            >
                                <Edit3 size={12} /> EDIT
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function DetailBlock({ icon: Icon, label, value, accent, muted }) {
    return (
        <div className="bg-virtus-bg p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-3">
                <Icon size={12} />
                {label}
            </div>
            <div
                className={muted ? "italic text-base" : "font-mono text-2xl font-bold"}
                style={{ color: muted ? "#5A5A5A" : (accent || "#FFFFFF") }}
            >
                {value}
            </div>
        </div>
    );
}

function dueAccent(dueDate, status) {
    if (!dueDate || status === "delivered") return null;
    try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dueDate);
        if (due < today) return "#FF1744";
        const diff = (due - today) / (1000 * 60 * 60 * 24);
        if (diff <= 2) return "#FFEA00";
    } catch (_) {}
    return null;
}
